import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Star, Calendar, MapPin, CheckCircle2, Trash2, Share2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { formatDateFR, findCategory } from "@/lib/constants";
import { b64ToSrc } from "@/lib/img";
import StoryShareDialog from "@/components/StoryShareDialog";

const LONG_PRESS_MS = 550;

export default function EventCard({ event, onFollow, onJoin, onLockedClick }) {
    const cat = findCategory(event.category);
    const Icon = cat?.icon;
    const isMenu = event.mode === "restaurant";
    const title = event.title || event.restaurant_name;
    const isMine = event.my_participation;
    const followed = isMine?.status === "followed";
    const joined = isMine?.status === "joined";
    const [storyOpen, setStoryOpen] = useState(false);
    const pressTimer = useRef(null);
    const longPressed = useRef(false);

    const startPress = () => {
        longPressed.current = false;
        pressTimer.current = setTimeout(() => {
            longPressed.current = true;
            setStoryOpen(true);
            // Haptic feedback on supported devices
            if (typeof navigator !== "undefined" && navigator.vibrate) {
                try { navigator.vibrate(30); } catch { /* ignore */ }
            }
        }, LONG_PRESS_MS);
    };
    const cancelPress = () => {
        if (pressTimer.current) {
            clearTimeout(pressTimer.current);
            pressTimer.current = null;
        }
    };
    const handleClick = (e) => {
        if (longPressed.current) {
            e.preventDefault();
            e.stopPropagation();
            longPressed.current = false;
            return;
        }
        if (onLockedClick) {
            e.preventDefault();
            e.stopPropagation();
            onLockedClick(event);
        }
    };
    return (
        <>
        <Link to={`/events/${event.id}`} data-testid={`event-card-${event.id}`}
            onClick={handleClick}
            onTouchStart={startPress}
            onTouchEnd={cancelPress}
            onTouchMove={cancelPress}
            onMouseDown={startPress}
            onMouseUp={cancelPress}
            onMouseLeave={cancelPress}
            onContextMenu={(e) => { e.preventDefault(); setStoryOpen(true); }}
            className="discover-card overflow-hidden block group relative select-none">
            {event.deleted && (
                <div className="absolute inset-0 bg-red-500/20 backdrop-blur-sm z-10 flex items-center justify-center">
                    <span className="px-4 py-2 rounded-full bg-red-600 text-white font-black text-lg">SUPPRIME</span>
                </div>
            )}
            {event.modifiéd_stamp && (
                <span className="absolute top-3 right-3 z-10 px-2 py-1 text-[10px] font-bold rounded-full bg-amber-500 text-white shadow">
                    MODIFIE
                </span>
            )}
            <div className="event-thumb bg-discover-soft">
                {/* iter58 — Prefer the new file-backed URL over the
                    legacy inline base64. `flyer_url` is a small string
                    like "/api/f/abcd…jpg" served with a 1-year CDN
                    cache. Falls back to `flyer_base64` (thumbnail) for
                    legacy events that still have it inline in Mongo. */}
                {event.flyer_url ? (
                    <img src={event.flyer_url} alt={title}
                        loading="lazy" decoding="async"
                        className="transition-transform group-hover:scale-105" />
                ) : event.flyer_base64 ? (
                    <img src={b64ToSrc(event.flyer_base64)} alt={title}
                        loading="lazy" decoding="async"
                        className="transition-transform group-hover:scale-105" />
                ) : (
                    <div className="event-thumb-fallback flex items-center justify-center bg-discover-gradient">
                        {Icon && <Icon size={56} className="text-white/90" strokeWidth={1.3} />}
                    </div>
                )}
                {event.gratuit && (
                    <Badge className="absolute bottom-3 left-3 bg-emerald-500 text-white border-0 rounded-full">
                        Gratuit
                    </Badge>
                )}
                {cat && (
                    <Badge className="absolute top-3 left-3 bg-white/90 text-foreground border-0 rounded-full">
                        {Icon && <Icon size={12} className="mr-1" />}{cat.label}
                    </Badge>
                )}
                <button type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); setStoryOpen(true); }}
                    className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/90 dark:bg-black/60 backdrop-blur flex items-center justify-center opacity-0 group-hover:opacity-100 transition shadow"
                    aria-label="Partager en story"
                    data-testid={`event-${event.id}-share-btn`}>
                    <Share2 size={14} />
                </button>
            </div>
            <div className="p-4 space-y-2">
                <h3 className="font-bold text-base line-clamp-1">{title}</h3>
                {isMenu && event.restaurant_name && event.restaurant_hours && (
                    <p className="text-xs text-muted-foreground line-clamp-1">{event.restaurant_hours}</p>
                )}
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Calendar size={12} />{formatDateFR(event.date)}{event.heure_debut ? ` - ${event.heure_debut}` : ""}</span>
                    <span className="flex items-center gap-1"><MapPin size={12} />{event.city}</span>
                </div>
                {(onFollow || onJoin) && (
                    <div className="flex gap-2 pt-2" onClick={(e) => e.preventDefault()}>
                        <button onClick={(e) => { e.preventDefault(); onFollow?.(event.id); }}
                            className={`flex-1 px-3 py-2 rounded-full text-xs font-semibold transition flex items-center justify-center gap-1
                                ${followed ? "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-200" : "bg-muted hover:bg-accent"}`}
                            data-testid={`event-${event.id}-follow-btn`}>
                            <Star size={13} className={followed ? "fill-amber-500 text-amber-500" : ""} />
                            {followed ? "Suivi" : "Suivre"}
                        </button>
                        <button onClick={(e) => { e.preventDefault(); onJoin?.(event.id); }}
                            className={`flex-1 px-3 py-2 rounded-full text-xs font-semibold transition flex items-center justify-center gap-1
                                ${joined ? "bg-emerald-500 text-white" : "bg-discover-gradient text-white"}`}
                            data-testid={`event-${event.id}-join-btn`}>
                            <CheckCircle2 size={13} />
                            {joined ? "Rejoint" : "Rejoindre"}
                        </button>
                    </div>
                )}
            </div>
        </Link>
        <StoryShareDialog event={event} open={storyOpen} onOpenChange={setStoryOpen} />
        </>
    );
}
